import React, { useRef, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { Box, Chip, Tooltip } from '@mui/material';
import { makeStyles } from "@mui/styles";
import NoDataMessage from './NoDataMessage';
import LoadingChart from './LoadingChart';
import { ExportButton } from './StrategyContent';

const useStyles = makeStyles(() => ({
  container: {
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '10px',
  },
  chartContainer: {
    flex: 1,
    position: 'relative',
    width: '100%',
    minHeight: '40vh',
    '@media (max-width: 600px)': {
      minHeight: '30vh',
    },
    '@media (min-width: 700px)': {
        minHeight: '28vh',
      },
    '@media (min-width: 1200px)': {
      minHeight: '28vh',
    },
  },
}));

const PerformanceChart = ({ data, options, isFullscreen, isLoading = false, showDataInfo = true }) => {
  const classes = useStyles();
  const chartRef = useRef(null);

    useEffect(() => {
    const resizeChart = () => {
      if (chartRef.current) {
        chartRef.current.resize();
      }
    };

    resizeChart();
    window.addEventListener('resize', resizeChart);

    return () => {
      window.removeEventListener('resize', resizeChart);
    };
  }, [isFullscreen]);

  useEffect(() => {
        const handleDoubleClick = (event) => {
      event.preventDefault();
      if (chartRef.current) {
        try {
          if (chartRef.current.resetZoom) {
            chartRef.current.resetZoom('default');
          } else if (chartRef.current.chart?.resetZoom) {
            chartRef.current.chart.resetZoom('default');
          } else {
            // Manual reset by updating scales
            const chart = chartRef.current;
            if (chart.options?.scales) {
              Object.keys(chart.options.scales).forEach(scaleId => {
                delete chart.options.scales[scaleId].min;
                delete chart.options.scales[scaleId].max;
              });
              chart.update('none');
            }
          }
        } catch (error) {
        }
      }
    };

    // Add delay to ensure chart is fully rendered
    const timer = setTimeout(() => {
      const chartCanvas = chartRef.current?.canvas;
      if (chartCanvas) {
        chartCanvas.addEventListener('dblclick', handleDoubleClick);

        chartCanvas._doubleClickHandler = handleDoubleClick;
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const chartCanvas = chartRef.current?.canvas;
      if (chartCanvas && chartCanvas._doubleClickHandler) {
        chartCanvas.removeEventListener('dblclick', chartCanvas._doubleClickHandler);
        delete chartCanvas._doubleClickHandler;
      }
    };
  }, [data]);

  if (isLoading) {
    return <LoadingChart message="Loading performance chart..." />;
  }

  if (!data || data.labels.length === 0) {
    return <NoDataMessage message="Please click calculate button on settings." />;
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.headerContainer} style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        {showDataInfo && data.originalDataLength && data.displayDataLength && data.originalDataLength !== data.displayDataLength && (
          <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Tooltip title={`Showing ${data.displayDataLength} points from ${data.originalDataLength} total data points for better performance`}>
              <Chip
                label={`${data.displayDataLength}/${data.originalDataLength} points`}
                size="small"
                variant="outlined"
                color="info"
              />
            </Tooltip>
          </Box>
        )}
        <ExportButton chartId="performanceChartExport" />
      </Box>
      <Box className={classes.chartContainer}>
        <div id="performanceChartExport" style={{ height: '100%', width: '100%' }}>
          <Line
            data={data}
            options={{
              ...options,
              maintainAspectRatio: false,
              responsive: true,
            }}
            ref={chartRef}
          />
        </div>
      </Box>
    </Box>
  );
};

export default PerformanceChart;